'use client';

// ═══════════════════════════════════════════════════════════════
// useCart Hook - Bridge to ecoBuilder Cart System
// ═══════════════════════════════════════════════════════════════
//
// IMPORTANT: This is a BRIDGE to the unified cart system.
// We re-export from the main cart hook to ensure:
// 1. Theme and ecoBuilder share the SAME cart storage
// 2. Items added in Theme appear in Checkout
// 3. Single source of truth for cart state
//
// ═══════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useCart as useEcoBuilderCart, CartItem as EcoBuilderCartItem } from '@/hooks/use-cart';
import type { Product, ProductVariant } from '../theme-contract';

// ─────────────────────────────────────────────────────────────
// Types - Compatible interface with Theme components
// ─────────────────────────────────────────────────────────────

export interface CartItem {
    id: string;
    productId: string;
    name: string;
    slug: string;
    price: number;
    image?: string;
    variant?: {
        id: string;
        name: string;
        price?: number;
        options?: Record<string, string>;
    };
    quantity: number;
    currency: string;
}

interface UseCartReturn {
    /** Cart items (mapped to Theme format) */
    items: CartItem[];

    /** Total number of items */
    count: number;

    /** Total price */
    total: number;

    /** Currency code (from first item) */
    currency: string;

    /** Is cart empty */
    isEmpty: boolean;

    /** Is cart drawer open */
    isOpen: boolean;

    /** Open/close cart drawer */
    setIsOpen: (open: boolean) => void;

    /** Toggle cart drawer */
    toggleCart: () => void;

    /** Add product to cart */
    addToCart: (product: Product, variant?: ProductVariant, quantity?: number) => void;

    /** Remove item from cart */
    removeFromCart: (itemId: string) => void;

    /** Update item quantity */
    updateQuantity: (itemId: string, quantity: number) => void;

    /** Clear the cart */
    clearCart: () => void;

    /** Is cart loading */
    isLoading: boolean;
}

// ─────────────────────────────────────────────────────────────
// Hook Implementation - Bridges to ecoBuilder cart
// ─────────────────────────────────────────────────────────────

/**
 * Cart management hook - DELEGATES to ecoBuilder cart system
 * This ensures Theme and Checkout share the same cart!
 */
export function useCart(): UseCartReturn {
    const ecoCart = useEcoBuilderCart();

    // Map ecoBuilder items to Theme format
    const items: CartItem[] = useMemo(() => {
        return ecoCart.items.map((item: EcoBuilderCartItem) => {
            // Build variant display name from options
            const variantOptions = (item as any).variantOptions as Record<string, string> | undefined;
            const variantDisplayName = variantOptions
                ? Object.entries(variantOptions).map(([k, v]) => `${k}: ${v}`).join(' / ')
                : item.variant || '';

            return {
                id: item.id,
                productId: item.id.includes('::') ? item.id.split('::')[0]! : item.id,
                name: item.name,
                slug: (item as any).slug || item.id.split('::')[0],
                price: item.price,
                image: item.image,
                variant: (item.variant || variantOptions) ? {
                    id: (item as any).variantId || item.variant || '',
                    name: variantDisplayName,
                    price: item.price,
                    options: variantOptions,
                } : undefined,
                quantity: item.quantity,
                currency: (item as any).currency || 'DZD',
            };
        });
    }, [ecoCart.items]);

    // Computed values
    const count = ecoCart.getTotalItems();
    const total = ecoCart.getSubtotal();
    const isEmpty = items.length === 0;
    const currency = items[0]?.currency || 'DZD';

    // Bridge addToCart - converts Theme Product to ecoBuilder format
    const addToCart = (product: Product, variant?: ProductVariant, quantity: number = 1) => {
        const productPrice = Number(product.price) || 0;
        // Only use variant price if it's explicitly set and positive
        // If variant price is 0, null, or undefined, fall back to product price
        const variantPrice = variant?.price != null && Number(variant.price) > 0 ? Number(variant.price) : undefined;
        const finalPrice = variantPrice !== undefined ? variantPrice : productPrice;

        // Build variant display name from options
        const variantOptions = variant?.options as Record<string, string> | undefined;
        const variantName = variantOptions
            ? Object.entries(variantOptions).map(([k, v]) => `${k}: ${v}`).join(' / ')
            : variant?.name || '';

        // Use variant image if available, otherwise product image
        const image = variant?.image_url || product.featured_image || product.images?.[0] || '/placeholder.png';

        const itemToAdd = {
            id: product.id,
            name: product.name,
            price: finalPrice,
            image,
            quantity,
            maxStock: variant?.stock_quantity ?? (product as any).inventory_quantity ?? (product as any).stock_quantity ?? 99,
            variant: variantName,
            variantId: variant?.id,
            variantOptions: variantOptions,
            slug: product.slug || product.id,
            currency: product.currency || 'DZD',
        };

        ecoCart.addItem(itemToAdd);
    };

    // Wrappers for other actions
    const removeFromCart = (itemId: string) => {
        ecoCart.removeItem(itemId);
    };

    const setIsOpen = (open: boolean) => {
        if (open) {
            ecoCart.openCart();
        } else {
            ecoCart.closeCart();
        }
    };

    return {
        items,
        count,
        total,
        currency,
        isEmpty,
        isOpen: ecoCart.isOpen,
        setIsOpen,
        toggleCart: ecoCart.toggleCart,
        addToCart,
        removeFromCart,
        updateQuantity: ecoCart.updateQuantity,
        clearCart: ecoCart.clearCart,
        isLoading: false, // ecoBuilder cart doesn't have loading state
    };
}

export default useCart;
